/**
 * @lines98/client/services/replay-api — typed wrapper вокруг
 * `GET /api/replay/:short_id` (T-9 server endpoint; brief-35 §A.2).
 *
 * Per replays-ghosts.md §2.6 + §5.4 + anti-cheat.md §3A.4: the replay URL is a
 * PUBLIC read-endpoint that works for guests AND authenticated viewers. Bearer
 * (api-client.ts, ADR-0006 §D2) is attached automatically when an identity
 * exists — that's what lets the OWNER get the own-replay carve-out (§3A.2,
 * PLAYABLE сразу), while a foreign viewer of TODAY's daily gets a TEASER.
 *
 * **Foundational invariant Anti-shadow-practice (server-enforced, NOT here).**
 * The PLAYABLE-vs-TEASER decision is the gate (`replay/gate.ts`, anti-cheat.md
 * §3A.4), enforced ENTIRELY server-side: a teaser response physically lacks
 * `moves`/`seed`/final-frame, so no client trick can pry the playable payload
 * out. This wrapper does NOT add any «if today, show teaser» guard — it just
 * camelCase-maps whichever shape the server returned (replays-ghosts.md §5.5
 * «client renders by server kind»).
 *
 * Error handling mirrors leaderboard-api.ts: a discriminated union. The route
 * always answers (replays-ghosts.md §2.6 AC) — a 404 is the typed `not_found`
 * branch (NOT a thrown error / blank), so the caller renders «Реплей недоступен»
 * + CTA (AC-10).
 *
 * Cross-references:
 *   - packages/server/src/routes/replay.ts (contract source — JSON shape).
 *   - services/api-client.ts (Bearer injection + envelope shape).
 *   - replays-ghosts.md §2.6 / §5.4 / §5.5 / AC-6 / AC-10 / AC-12 / AC-13 / AC-14.
 *   - anti-cheat.md §3A.4 (gate — server-enforced).
 */

import type { FinishReason, GameMode, Move } from '@lines98/shared/types/game';

import { apiClient } from './api-client.js';

// =============================================================================
// Public types (camelCase — mapped from snake_case wire shape)
// =============================================================================

/**
 * A playable replay payload (camelCase-mapped). `seed + moves[]` are the
 * canonical game (replays-ghosts.md §2.1) — the player re-simulates them through
 * the SHARED engine. All other fields are display echo.
 */
export interface PlayableReplay {
  readonly shortId: string;
  readonly seed: number;
  readonly moves: Move[];
  readonly mode: GameMode;
  readonly scoreVerified: number;
  readonly linesVerified: number;
  // Display echo — any persisted reason (incl. `restart`, legacy `new_game`).
  readonly finishReason: FinishReason;
  /** ISO-8601 UTC. */
  readonly finishedAt: string;
  /** YYYY-MM-DD for daily games, null otherwise. */
  readonly dateOfDay: string | null;
  readonly isDaily: boolean;
  /**
   * Принадлежит ли реплей текущему зрителю (server-truth: `replay.player_id == me`).
   * Решает показ кнопки-дискеты «Сохранить в Книгу» (тикет B AC-B1): дискета на ЧУЖОМ
   * (`isOwn === false`), НЕ на своём. Сервер вычисляет по Bearer-идентичности
   * (`GET /api/replay/:short_id`). `undefined` — старый сервер без поля → клиент
   * трактует remote playable как ЧУЖОЙ (deep-link/book/leaderboard открывают не свой
   * только-что сыгранный; свой open идёт локальным mountReplayScreen без сети). Когда
   * сервер начнёт отдавать `is_own`, клиент использует его как авторитет (НЕ guess).
   */
  readonly isOwn?: boolean;
  /**
   * Уже в АРХИВЕ «Чужие» текущего зрителя? (server-truth: EXISTS в `replay_bookmarks`).
   * Драйвит НАЧАЛЬНОЕ состояние дискеты «Сохранить» (owner-bug 2026-06-25): уже
   * сохранённый чужой реплей открывается с СЕРОЙ/disabled дискетой СРАЗУ, без клика
   * (передаётся в `attachReplaySaveButton({ savedInitially })`). Только для чужого
   * авторизованного зрителя; свой/гость → `false`. `undefined` — старый сервер без
   * поля → дискета стартует активной (прежнее поведение, fallback).
   */
  readonly bookmarked?: boolean;
}

/**
 * A teaser payload (camelCase-mapped). NO `moves`/`seed`/final-frame — the
 * foundational invariant forbids them on the wire to a TEASER viewer
 * (anti-cheat.md §3A.3). The type itself enforces this absence.
 */
export interface ReplayTeaser {
  readonly scoreVerified: number;
  /** Daily-board rank (1-based), or null if unranked. */
  readonly rank: number | null;
  readonly movesCount: number;
  /** Whole seconds until the next rollover (≥ 0) — when the teaser opens up. */
  readonly countdownToRolloverSeconds: number;
  readonly finishedAt: string;
  readonly isDaily: true;
  readonly dateOfDay: string | null;
}

export type ReplayResult =
  | { readonly ok: true; readonly kind: 'playable'; readonly replay: PlayableReplay }
  | { readonly ok: true; readonly kind: 'teaser'; readonly teaser: ReplayTeaser }
  /** 404 — the replay does not exist / was removed (AC-10). */
  | { readonly ok: false; readonly reason: 'not_found' }
  /** Network / fetch reject — retry-able. */
  | { readonly ok: false; readonly reason: 'network' }
  /** 5xx or unexpected response shape — retry-able. */
  | { readonly ok: false; readonly reason: 'server_error' };

// =============================================================================
// Internal wire shapes (server snake_case) — see routes/replay.ts
// =============================================================================

interface PlayableReplayWire {
  short_id: string;
  seed: number;
  moves: Move[];
  mode: GameMode;
  score_verified: number;
  lines_verified: number;
  finish_reason: FinishReason;
  finished_at: string;
  date_of_day: string | null;
  is_daily: boolean;
  /** Server-truth ownership (тикет B): задаётся когда сервер начнёт отдавать поле. */
  is_own?: boolean;
  /** Server-truth «уже в архиве «Чужие» зрителя» (owner-bug 2026-06-25): начальное серое
   *  состояние дискеты. Опционально — fallback для старого сервера без поля. */
  bookmarked?: boolean;
}

interface TeaserWire {
  score_verified: number;
  rank: number | null;
  moves_count: number;
  countdown_to_rollover_seconds: number;
  finished_at: string;
  is_daily: true;
  date_of_day: string | null;
}

type ReplayWireResponse =
  | { ok: false; reason: 'not_found' }
  | { ok: true; kind: 'playable'; replay: PlayableReplayWire }
  | { ok: true; kind: 'teaser'; teaser: TeaserWire; cta: { play_daily: true } };

// =============================================================================
// Public API
// =============================================================================

/**
 * Fetch a replay by its `short_id` (the `/r/<short_id>` URL key, ADR-0003).
 *
 * Bearer is attached automatically by api-client.ts if an identity exists — that
 * grants the OWNER the playable carve-out. Works for guests too (they get
 * playable for past/non-daily, teaser for a foreign today's daily).
 *
 * @returns `ReplayResult` discriminated union. The caller branches on `.ok` then
 *   on `.kind` (PLAYABLE → player, TEASER → teaser screen). The branch is the
 *   SERVER's decision — never re-derived client-side.
 */
export async function getReplay(shortId: string): Promise<ReplayResult> {
  const path = `/api/replay/${encodeURIComponent(shortId)}`;

  let response;
  try {
    response = await apiClient.get<ReplayWireResponse>(path);
  } catch {
    // ApiClientError (network reject) — caller can retry.
    return { ok: false, reason: 'network' };
  }

  // 404 → the typed not_found branch (the link always answers — §2.6 AC). The
  // server sends `{ ok:false, reason:'not_found' }` with a 404 status; both the
  // status AND the typed body confirm it.
  if (!response.ok) {
    if (response.status === 404) return { ok: false, reason: 'not_found' };
    return { ok: false, reason: 'server_error' };
  }

  const body = response.body;
  if (typeof body !== 'object' || body === null) {
    return { ok: false, reason: 'server_error' };
  }

  // Some servers may return 200 with `{ ok:false, reason:'not_found' }` (defensive
  // — current contract uses 404, but render the typed branch either way).
  const wire = body as ReplayWireResponse;
  if (wire.ok === false) {
    return wire.reason === 'not_found'
      ? { ok: false, reason: 'not_found' }
      : { ok: false, reason: 'server_error' };
  }

  if (wire.kind === 'playable') {
    const r = wire.replay;
    if (!r || typeof r !== 'object' || !Array.isArray(r.moves)) {
      return { ok: false, reason: 'server_error' };
    }
    return {
      ok: true,
      kind: 'playable',
      replay: {
        shortId: r.short_id,
        seed: r.seed >>> 0,
        moves: r.moves,
        mode: r.mode,
        scoreVerified: r.score_verified,
        linesVerified: r.lines_verified,
        finishReason: r.finish_reason,
        finishedAt: r.finished_at,
        dateOfDay: r.date_of_day ?? null,
        isDaily: r.is_daily === true,
        // ownership (тикет B): отражаем server-truth когда поле есть; иначе undefined
        // (caller трактует remote playable как чужой — см. PlayableReplay.isOwn док).
        ...(typeof r.is_own === 'boolean' ? { isOwn: r.is_own } : {}),
        // «уже сохранён» (owner-bug 2026-06-25): server-truth начального состояния дискеты;
        // отсутствие поля (старый сервер) → undefined → дискета стартует активной (fallback).
        ...(typeof r.bookmarked === 'boolean' ? { bookmarked: r.bookmarked } : {}),
      },
    };
  }

  if (wire.kind === 'teaser') {
    const t = wire.teaser;
    if (!t || typeof t !== 'object') {
      return { ok: false, reason: 'server_error' };
    }
    return {
      ok: true,
      kind: 'teaser',
      teaser: {
        scoreVerified: t.score_verified,
        rank: t.rank ?? null,
        movesCount: t.moves_count,
        countdownToRolloverSeconds: t.countdown_to_rollover_seconds,
        finishedAt: t.finished_at,
        isDaily: true,
        dateOfDay: t.date_of_day ?? null,
      },
    };
  }

  // Unrecognized kind — defensive.
  return { ok: false, reason: 'server_error' };
}
